import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  fetchSubscriptionCompletion,
  SubscriptionCompletion,
} from '../api/subscription';
import { useAuth } from '../contexts/AuthContext';

const COMPLETION_POLL_INTERVAL_MS = 2000;
const MAX_AUTOMATIC_POLLS = 5;
const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{8,128}$/u;

type CompletionView =
  | { kind: 'loading'; message: string }
  | { kind: 'confirmed'; message: string }
  | { kind: 'pending'; message: string }
  | { kind: 'failed'; message: string };

const containsClientSecretMarker = (value: string): boolean => {
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    // Keep the original value when a malformed escape sequence is present.
  }
  return decoded.toLowerCase().replace(/[-_]/gu, '').includes('clientsecret');
};

const removeStripeSecretsFromBrowserUrl = (): void => {
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of Array.from(url.searchParams.keys())) {
    if (containsClientSecretMarker(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (containsClientSecretMarker(url.hash)) {
    url.hash = '';
    changed = true;
  }
  if (changed) {
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
  }
};

const COMPLETION_MESSAGES: Record<
  SubscriptionCompletion['state'],
  Record<string, string>
> = {
  confirmed: {
    subscription_confirmed:
      'サブスクリプションの有効化を確認しました。ログイン状態のままご利用いただけます。',
    payment_succeeded:
      'お支払いとサブスクリプションの有効化を確認しました。ログイン状態のままご利用いただけます。',
    replacement_scheduled:
      'プラン切替の予約を確認しました。現在の契約期間終了後に新しいプランへ切り替わります。',
    setup_intent_succeeded:
      'お支払い方法の確認が完了しました。サブスクリプション画面で契約手続きの状態をご確認ください。',
  },
  pending: {
    payment_processing: '決済処理が継続中です。この画面から状態を再確認できます。',
    payment_pending: 'お支払いの反映を待っています。この画面から状態を再確認できます。',
    requires_action: '追加の本人認証が必要です。決済画面の案内を完了してから再確認してください。',
    invoice_pending: '銀行振込の入金確認を待っています。反映後に契約状態が更新されます。',
    subscription_pending: 'サブスクリプションの反映を待っています。この画面から状態を再確認できます。',
    replacement_pending: 'プラン切替の予約処理が継続中です。この画面から状態を再確認できます。',
    setup_intent_pending:
      'お支払い方法の確認処理が継続中です。認証を完了してから再確認してください。',
    checkout_pending:
      'Stripe決済画面での処理完了を待っています。処理後にこの画面で再確認してください。',
  },
  failed: {
    payment_failed: 'カード決済を完了できませんでした。お支払い方法を確認して再度お試しください。',
    operation_failed: '決済または契約処理を完了できませんでした。内容を確認して再度お試しください。',
    subscription_failed:
      'サブスクリプションを有効化できませんでした。時間をおいて再度お試しください。',
    invoice_failed: '銀行振込用の請求書を発行できませんでした。内容を確認して再度お試しください。',
    replacement_canceled: 'プラン切替の予約はキャンセルされました。必要に応じて再度お申し込みください。',
    setup_intent_canceled: 'お支払い方法の確認はキャンセルされました。再度お試しください。',
    checkout_expired:
      'Stripe決済画面の有効期限が切れました。サブスクリプション画面から再度お試しください。',
  },
};

const messageForCompletion = (completion: SubscriptionCompletion): string => {
  const knownMessage = completion.messageCode
    ? COMPLETION_MESSAGES[completion.state][completion.messageCode]
    : undefined;
  if (knownMessage) {
    return knownMessage;
  }
  if (completion.state === 'confirmed') {
    return '決済または契約処理の完了を確認しました。サブスクリプション画面で現在の契約状態をご確認ください。';
  }
  if (completion.state === 'failed') {
    return '決済または契約処理を完了できませんでした。内容を確認して再度お試しください。';
  }
  return '決済結果を確認しています。この画面を閉じずにお待ちください。';
};

const SubscriptionSuccess: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkSubscriptionStatus } = useAuth();
  const [retryToken, setRetryToken] = useState(0);
  const [view, setView] = useState<CompletionView>({
    kind: 'loading',
    message: '決済結果を確認しています。',
  });

  const operationId = useMemo(() => {
    const queryOperationId = new URLSearchParams(location.search).get('operation_id')?.trim();
    return queryOperationId && OPERATION_ID_PATTERN.test(queryOperationId) ? queryOperationId : null;
  }, [location.search]);

  useEffect(() => {
    removeStripeSecretsFromBrowserUrl();
  }, []);

  useEffect(() => {
    if (!operationId) {
      setView({
        kind: 'failed',
        message:
          '購入完了を確認するための処理IDがありません。サブスクリプション画面から状態をご確認ください。',
      });
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    let attempt = 0;

    const verify = async (): Promise<void> => {
      setView((current) =>
        current.kind === 'pending'
          ? current
          : { kind: 'loading', message: '決済結果を確認しています。' },
      );
      try {
        const completion = await fetchSubscriptionCompletion(operationId);
        if (cancelled) {
          return;
        }

        const nextView: CompletionView = {
          kind: completion.state,
          message: messageForCompletion(completion),
        };
        setView(nextView);

        if (completion.state === 'pending' && attempt < MAX_AUTOMATIC_POLLS - 1) {
          attempt += 1;
          timer = window.setTimeout(() => {
            void verify();
          }, COMPLETION_POLL_INTERVAL_MS);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        setView({
          kind: 'failed',
          message:
            error instanceof Error
              ? error.message
              : '契約状態を確認できませんでした。時間をおいて再度お試しください。',
        });
      }
    };

    void verify();

    return () => {
      cancelled = true;
      if (timer !== undefined) {
        window.clearTimeout(timer);
      }
    };
  }, [operationId, retryToken]);

  const retry = () => {
    setView({ kind: 'loading', message: '決済結果を確認しています。' });
    setRetryToken((current) => current + 1);
  };

  const navigateToSubscription = async (): Promise<void> => {
    try {
      await checkSubscriptionStatus();
    } catch {
      // A refresh failure must not block navigation or replace the authenticated session.
    }
    navigate('/subscription');
  };

  const title =
    view.kind === 'confirmed'
      ? '決済・契約処理を確認しました'
      : view.kind === 'pending' || view.kind === 'loading'
        ? '決済結果を確認しています'
        : '購入完了を確認できません';

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Stack spacing={2.5} alignItems="center">
          {(view.kind === 'loading' || view.kind === 'pending') && (
            <CircularProgress aria-label="決済結果を確認中" />
          )}
          <Typography variant="h5">{title}</Typography>
          <Alert
            severity={
              view.kind === 'confirmed'
                ? 'success'
                : view.kind === 'failed'
                  ? 'error'
                  : 'info'
            }
            sx={{ width: '100%', textAlign: 'left' }}
          >
            {view.message}
          </Alert>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {view.kind !== 'confirmed' && (
              <Button variant="contained" onClick={retry} disabled={view.kind === 'loading'}>
                状態を再確認
              </Button>
            )}
            <Button variant="outlined" onClick={() => void navigateToSubscription()}>
              サブスクリプション画面へ
            </Button>
            <Button variant="text" onClick={() => navigate('/')}>
              ホームへ
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
};

export default SubscriptionSuccess;
